/**
 * Built-in system predicates (#173, wave:identity-v2 — ADR-0004 §2/§3).
 *
 * ADR-0004 draws a boundary between **open vocabulary** (predicates, traits,
 * tuple fields, enum values, calendars — identified by an opaque, minted,
 * kind-prefixed id) and **system-closed, code-typed** identifiers that the
 * engine references directly and the user can never extend (entity roots,
 * config keys, and the one built-in predicate, `name`).
 *
 * `name` is that **single built-in system predicate**: the engine renders
 * every entity title from it (`apps/client/src/entities/list-view.ts`
 * `displayName()`). Per §2 it is the **one grandfathered system token** —
 * it keeps the stable, readable id `'name'` (NOT a minted `pred_<uuidv4>`),
 * because code references it by that literal and it is never user-authored,
 * extracted, or removable. It is present in **every** campaign with **zero**
 * pack subscriptions (§3: "a brand-new campaign is not empty: it has the
 * built-in `name`, its settings, and progressive canonicity").
 *
 * Transitional coexistence: `core.json` still declares a `name` predicate
 * with the same id (its removal is #183). Because the id is identical,
 * registering the built-in over (or under) the core.json copy is a pure
 * last-writer overwrite of the same registry key — no conflict, no
 * duplicate. Once #183 lands, this constant is the sole source of `name`.
 */
import type { PredicateDeclaration } from './predicate.js';

/**
 * The stable, grandfathered id of the built-in `name` system predicate
 * (ADR-0004 §2). The single readable token exempt from the opaque-id rule.
 * Title rendering references THIS constant rather than a scattered `'name'`
 * string literal (see `list-view.ts` `displayName()`).
 */
export const BUILTIN_NAME_PREDICATE_ID = 'name';

/**
 * The built-in `name` system predicate (ADR-0004 §2). A plain `text`
 * predicate carrying a mandatory `LocalizedString` name.
 *
 * Constructed directly as a typed `PredicateDeclaration` rather than via
 * `PredicateDeclarationSchema.parse`: the schema validates `id` against
 * `PredicateIdSchema` (`pred_<uuidv4>`), which deliberately rejects the
 * grandfathered token `'name'`. The boundary is intentional — open
 * vocabulary is minted/validated, this one system predicate is a code
 * constant. The object below is type-checked against `PredicateDeclaration`,
 * so any drift in the declaration shape is caught at compile time.
 */
export const BUILTIN_NAME_PREDICATE: PredicateDeclaration = {
  id: BUILTIN_NAME_PREDICATE_ID,
  value_kind: ['text'],
  // `name` is the canonical single-value title; a later write supersedes
  // the prior one through the rebase merge path (a city's rename history is
  // meaningful — entity `name` is a fact, not a setting; ADR-0004 §4).
  merge: 'rebase',
  // No identity-bearing aliases (opaque-id model, ADR-0004 §1); the field
  // is required on the declaration shape, so present as an empty list.
  previous_names: [],
  // Mandatory localized display name (ADR-0004 §1). The raw id is never
  // user-facing; this is what the UI shows for the predicate.
  label: { en: 'Name' },
  // §7.2 — a controlling player has direct authority over their PC's name.
  // The flag is necessary-but-not-sufficient: the op-accept gate also requires
  // the player to control the subject, so this never lets a player rename
  // (say) a Location they don't control.
  player_authority: true,
};

/**
 * Every built-in system predicate. Today this is exactly `name` (ADR-0004
 * §2: "the built-in predicate set shrinks to one"). Kept as a list so the
 * registration loop and any consumer that needs "the system predicates"
 * stays a single edit if the closed set ever grows.
 */
export const BUILTIN_PREDICATES: readonly PredicateDeclaration[] = [BUILTIN_NAME_PREDICATE];

/**
 * Add the built-in system predicates to a registry. Called when bootstrapping
 * EVERY campaign's registry, independent of any pack subscription — so a
 * zero-pack campaign still resolves entity titles via the built-in `name`
 * (ADR-0004 §2/§3).
 *
 * Idempotent: each built-in is keyed by its stable id, so re-running (or
 * running alongside core.json's transitional same-id declaration) is a pure
 * last-writer overwrite — never a duplicate, never a throw.
 */
export function registerBuiltins(registry: {
  add(declaration: PredicateDeclaration): void;
}): void {
  for (const decl of BUILTIN_PREDICATES) {
    registry.add(decl);
  }
}
